// components/shared/ErrorState.tsx
import type { ComponentType, ReactNode } from 'react';
import {
  AlertCircle,
  WifiOff,
  ServerCrash,
  Search,
  RefreshCw,
  Home,
  ChevronLeft,
  FileX,
  UserX,
  Lock,
  Clock,
  type LucideIcon,
} from 'lucide-react';

export type ErrorStateType =
  | 'network'
  | 'server'
  | 'notFound'
  | 'unauthorized'
  | 'forbidden'
  | 'timeout'
  | 'empty'
  | 'generic';

interface ErrorTypeConfig {
  icon: LucideIcon;
  title: string;
  message: string;
  iconColor: string;
  bgColor: string;
  borderColor: string;
}

const ERROR_CONFIGS: Record<ErrorStateType, ErrorTypeConfig> = {
  network: {
    icon: WifiOff,
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
  server: {
    icon: ServerCrash,
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
  notFound: {
    icon: FileX,
    title: 'Not Found',
    message: "We couldn't find what you're looking for.",
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
  unauthorized: {
    icon: Lock,
    title: 'Access Denied',
    message: "You don't have permission to view this content.",
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  forbidden: {
    icon: UserX,
    title: 'Forbidden',
    message: "You're not authorized to perform this action.",
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
  timeout: {
    icon: Clock,
    title: 'Request Timeout',
    message: 'The request took too long. Please try again.',
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  empty: {
    icon: Search,
    title: 'No Results Found',
    message: "We couldn't find any items matching your criteria.",
    iconColor: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
  generic: {
    icon: AlertCircle,
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again.',
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
};

export type ErrorStateSize = 'small' | 'medium' | 'large';

export interface ErrorAction {
  label: string;
  onClick: () => void;
  primary?: boolean;
  icon?: LucideIcon;
}

export interface ErrorStateError {
  message?: string;
  status?: number;
  toString?: () => string;
  [key: string]: unknown;
}

export interface ErrorStateProps {
  type?: ErrorStateType;
  title?: string;
  message?: string;
  error?: Error | ErrorStateError | null;
  onRetry?: () => void;
  retryLabel?: string;
  showRetry?: boolean;
  onGoBack?: () => void;
  onGoHome?: () => void;
  actions?: ErrorAction[];
  size?: ErrorStateSize;
  className?: string;
  children?: ReactNode;
}

const ErrorState = ({
  type = 'generic',
  title: customTitle,
  message: customMessage,
  error,
  onRetry,
  retryLabel = 'Try Again',
  showRetry = true,
  onGoBack,
  onGoHome,
  actions = [],
  size = 'medium',
  className = '',
  children,
}: ErrorStateProps) => {
  const config = ERROR_CONFIGS[type] || ERROR_CONFIGS.generic;
  const Icon = config.icon;

  const title = customTitle || config.title;
  const message = customMessage || config.message;

  const isDevelopment = process.env.NODE_ENV === 'development';

  const sizeClasses: Record<ErrorStateSize, Record<string, string>> = {
    small: {
      wrapper: 'p-4',
      icon: 'h-8 w-8',
      title: 'text-base',
      message: 'text-sm',
      button: 'px-3 py-1.5 text-sm',
    },
    medium: {
      wrapper: 'p-8',
      icon: 'h-12 w-12',
      title: 'text-lg',
      message: 'text-base',
      button: 'px-4 py-2',
    },
    large: {
      wrapper: 'p-12',
      icon: 'h-16 w-16',
      title: 'text-xl',
      message: 'text-lg',
      button: 'px-6 py-3 text-lg',
    },
  };

  const sizes = sizeClasses[size];

  const errorMessage = error
    ? (error as ErrorStateError).message ?? error.toString?.() ?? String(error)
    : null;
  const errorStatus = error ? (error as ErrorStateError).status : undefined;

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className={`text-center ${sizes.wrapper} max-w-md`}>
        <div
          className={`mx-auto mb-4 p-3 rounded-full ${config.bgColor} ${config.borderColor} border`}
        >
          <Icon className={`${sizes.icon} ${config.iconColor}`} />
        </div>

        <h3 className={`font-semibold text-gray-900 mb-2 ${sizes.title}`}>{title}</h3>

        <p className={`text-gray-600 mb-6 ${sizes.message}`}>{message}</p>

        {isDevelopment && error && (
          <div className="mb-6 p-3 bg-gray-100 rounded-lg text-left">
            <p className="text-xs font-mono text-gray-700 break-all">{errorMessage}</p>
            {errorStatus != null && (
              <p className="text-xs font-mono text-gray-600 mt-1">Status: {errorStatus}</p>
            )}
          </div>
        )}

        {children && <div className="mb-6">{children}</div>}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showRetry && onRetry && (
            <button
              onClick={onRetry}
              className={`${sizes.button} bg-primary text-white rounded-md hover:bg-primary-800 transition-colors inline-flex items-center gap-2`}
            >
              <RefreshCw className="h-4 w-4" />
              {retryLabel}
            </button>
          )}

          {onGoBack && (
            <button
              onClick={onGoBack}
              className={`${sizes.button} bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors inline-flex items-center gap-2`}
            >
              <ChevronLeft className="h-4 w-4" />
              Go Back
            </button>
          )}

          {onGoHome && (
            <button
              onClick={onGoHome}
              className={`${sizes.button} bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors inline-flex items-center gap-2`}
            >
              <Home className="h-4 w-4" />
              Go Home
            </button>
          )}

          {actions.map((action, index) => {
            const ActionIcon = action.icon;
            return (
              <button
                key={index}
                onClick={action.onClick}
                className={`${sizes.button} ${
                  action.primary
                    ? 'bg-primary text-white hover:bg-primary-800'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                } rounded-md transition-colors inline-flex items-center gap-2`}
              >
                {ActionIcon && <ActionIcon className="h-4 w-4" />}
                {action.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export interface EmptyStateProps {
  title?: string;
  message?: string;
  icon?: ComponentType<{ className?: string }>;
  onAction?: () => void;
  actionLabel?: string;
  actionIcon?: ComponentType<{ className?: string }>;
  children?: ReactNode;
  className?: string;
}

export const EmptyState = ({
  title = 'No items found',
  message = 'There are no items to display.',
  icon: Icon = Search,
  onAction,
  actionLabel = 'Add Item',
  actionIcon: ActionIcon,
  children,
  className = '',
}: EmptyStateProps) => {
  return (
    <div className={`text-center py-12 px-4 ${className}`}>
      <Icon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-6 max-w-sm mx-auto">{message}</p>
      {children}
      {onAction && (
        <button
          onClick={onAction}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-800 transition-colors inline-flex items-center gap-2"
        >
          {ActionIcon && <ActionIcon className="h-4 w-4" />}
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export interface LoadingErrorProps {
  resource?: string;
  onRetry?: () => void;
  error?: Error | ErrorStateError | null;
  className?: string;
}

export const LoadingError = ({
  resource = 'data',
  onRetry,
  error,
  className = '',
}: LoadingErrorProps) => {
  return (
    <ErrorState
      type="server"
      title={`Failed to load ${resource}`}
      message={`We couldn't load the ${resource}. This might be a temporary issue.`}
      error={error}
      onRetry={onRetry}
      retryLabel={`Reload ${resource}`}
      className={className}
    />
  );
};

export interface NetworkErrorProps {
  onRetry?: () => void;
  className?: string;
}

export const NetworkError = ({ onRetry, className = '' }: NetworkErrorProps) => {
  return <ErrorState type="network" onRetry={onRetry} className={className} />;
};

export interface NotFoundErrorProps {
  resource?: string;
  onGoBack?: () => void;
  onGoHome?: () => void;
  className?: string;
}

export const NotFoundError = ({
  resource = 'page',
  onGoBack,
  onGoHome,
  className = '',
}: NotFoundErrorProps) => {
  return (
    <ErrorState
      type="notFound"
      title={`${resource.charAt(0).toUpperCase() + resource.slice(1)} not found`}
      message={`The ${resource} you're looking for doesn't exist or has been removed.`}
      showRetry={false}
      onGoBack={onGoBack}
      onGoHome={onGoHome}
      className={className}
    />
  );
};

export interface UnauthorizedErrorProps {
  onLogin?: () => void;
  className?: string;
}

export const UnauthorizedError = ({ onLogin, className = '' }: UnauthorizedErrorProps) => {
  return (
    <ErrorState
      type="unauthorized"
      showRetry={false}
      actions={
        onLogin
          ? [
              {
                label: 'Log In',
                onClick: onLogin,
                primary: true,
                icon: Lock,
              },
            ]
          : []
      }
      className={className}
    />
  );
};

/**
 * Helper function to determine error type from HTTP status.
 */
export const getErrorType = (status: number | undefined): ErrorStateType => {
  switch (status) {
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'notFound';
    case 408:
      return 'timeout';
    case 500:
    case 502:
    case 503:
      return 'server';
    default:
      if (!navigator.onLine) {
        return 'network';
      }
      return 'generic';
  }
};

export default ErrorState;
